import { useState } from 'react'
import { useIntl } from 'react-intl'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { formatPrice } from '@/lib/formatPrice'
import { useFormatMessage } from '@/translations/useFormatMessage'
import type { CalculationResponse } from '@/shared/types/cart'
import { useAppSelector } from '@/store'
import {
	selectCartItems,
	selectTipAmountInCents,
	selectDiscountCode,
	selectDiscountName,
	selectDiscountCodeValid,
	selectGiftCardAppliedAmount,
	selectGiftCardNumber,
	selectCouponDiscountAmount,
} from '@/store/ticketSlice'
import {
	AccordionRoot,
	BreakdownSection,
	BreakdownRows,
	BreakdownRow,
	BreakdownLabel,
	BreakdownValue,
	BreakdownDivider,
	AmountDueButton,
	AmountDueChevronWrap,
	AmountDueLabel,
	AmountDueValue,
	chevronIconSx,
} from './AmountDueAccordion.styled'

interface AmountDueAccordionProps {
	calculations: CalculationResponse | null
	currency: string
	defaultExpanded?: boolean
}

export function AmountDueAccordion({
	calculations,
	currency,
	defaultExpanded = false,
}: AmountDueAccordionProps) {
	// Uncontrolled: defaultExpanded sets the initial state only.
	// Changes to this prop after mount are ignored by design.
	const [expandedByUser, setExpandedByUser] = useState(defaultExpanded)
	const intl = useIntl()
	const { t } = useFormatMessage()

	const tipAmountInCents = useAppSelector(selectTipAmountInCents)
	const discountCode = useAppSelector(selectDiscountCode)
	const discountName = useAppSelector(selectDiscountName)
	const discountCodeValid = useAppSelector(selectDiscountCodeValid)
	const giftCardAppliedAmount = useAppSelector(selectGiftCardAppliedAmount)
	const giftCardNumber = useAppSelector(selectGiftCardNumber)
	// An empty cart always has calculations === null (the calculate thunk early-exits and
	// resets it), but the breakdown must still show Subtotal/Total at $0.00 rather than
	// rendering empty. Rows stay hidden while a non-empty cart's calculation is in flight.
	const isCartEmpty = useAppSelector(selectCartItems).length === 0

	const expanded = expandedByUser

	const grandTotal = calculations?.totals.grandTotal ?? 0

	const couponDiscountAmount = useAppSelector(selectCouponDiscountAmount)

	// totalWithTip: server grand total (post-coupon) + client tip, before gift card deduction.
	// Shown in the "Total" row so all charges are visible before the gift card offset.
	const totalWithTip = grandTotal + tipAmountInCents
	// dueAmount: what the customer actually owes = total (with tip) minus gift card applied.
	const dueAmount = Math.max(0, totalWithTip - (giftCardAppliedAmount ?? 0))

	const formattedTotalWithTip = calculations
		? formatPrice(totalWithTip, intl.locale, currency)
		: formatPrice(grandTotal, intl.locale, currency)
	const formattedAmountDue = calculations
		? formatPrice(dueAmount, intl.locale, currency)
		: formatPrice(grandTotal, intl.locale, currency)

	return (
		<AccordionRoot>
			<BreakdownSection
				id="cart-amount-due-breakdown"
				data-testid="cart-amount-due-breakdown"
				hidden={!expanded}
			>
				<BreakdownRows>
					{(calculations?.totals.subtotal !== undefined || isCartEmpty) && (
						<BreakdownRow>
							<BreakdownLabel>{t('checkout:cart:subtotal')}</BreakdownLabel>
							<BreakdownValue>
								{formatPrice(
									calculations?.totals.subtotal ?? 0,
									intl.locale,
									currency,
								)}
							</BreakdownValue>
						</BreakdownRow>
					)}
					{!!calculations?.totals.surchargeTotal &&
						calculations.totals.surchargeTotal > 0 &&
						(() => {
							const namedRows = (calculations?.surcharges ?? []).filter(
								(surcharge) => surcharge.amount > 0 && surcharge.name !== 'None',
							)
							return namedRows.length > 0 ? (
								namedRows.map((surcharge, index) => (
									<BreakdownRow key={`${surcharge.name}-${index}`}>
										<BreakdownLabel>{surcharge.name}</BreakdownLabel>
										<BreakdownValue>
											{formatPrice(surcharge.amount, intl.locale, currency)}
										</BreakdownValue>
									</BreakdownRow>
								))
							) : (
								<BreakdownRow key="surcharge-fallback">
									<BreakdownLabel>{t('checkout:cart:surcharge')}</BreakdownLabel>
									<BreakdownValue>
										{formatPrice(
											calculations.totals.surchargeTotal,
											intl.locale,
											currency,
										)}
									</BreakdownValue>
								</BreakdownRow>
							)
						})()}
					{!!calculations?.totals.taxTotal && calculations.totals.taxTotal > 0 && (
						<BreakdownRow>
							<BreakdownLabel>{t('checkout:cart:taxes')}</BreakdownLabel>
							<BreakdownValue>
								{formatPrice(calculations.totals.taxTotal, intl.locale, currency)}
							</BreakdownValue>
						</BreakdownRow>
					)}
					{discountCodeValid && couponDiscountAmount > 0 && discountCode && (
						<BreakdownRow>
							<BreakdownLabel>
								{discountName ? `${discountName} [${discountCode}]` : discountCode}
							</BreakdownLabel>
							<BreakdownValue>
								-{formatPrice(couponDiscountAmount, intl.locale, currency)}
							</BreakdownValue>
						</BreakdownRow>
					)}
					{tipAmountInCents > 0 && (
						<BreakdownRow>
							<BreakdownLabel>{t('checkout:cart:tips')}</BreakdownLabel>
							<BreakdownValue>
								{formatPrice(tipAmountInCents, intl.locale, currency)}
							</BreakdownValue>
						</BreakdownRow>
					)}
					{giftCardAppliedAmount !== null && giftCardAppliedAmount > 0 && (
						<BreakdownRow>
							<BreakdownLabel>
								{t('checkout:cart:giftCardApplied')}
								{giftCardNumber ? ` ****${giftCardNumber.slice(-4)}` : ''}
							</BreakdownLabel>
							<BreakdownValue>
								-{formatPrice(giftCardAppliedAmount, intl.locale, currency)}
							</BreakdownValue>
						</BreakdownRow>
					)}
					{(calculations !== null || isCartEmpty) && (
						<BreakdownRow>
							<BreakdownLabel>{t('checkout:cart:total')}</BreakdownLabel>
							<BreakdownValue>{formattedTotalWithTip}</BreakdownValue>
						</BreakdownRow>
					)}
				</BreakdownRows>

				<BreakdownDivider />
			</BreakdownSection>

			<AmountDueButton
				type="button"
				aria-expanded={expanded}
				aria-controls="cart-amount-due-breakdown"
				aria-label={t('checkout:cart:amountDue:ariaLabel', { amount: formattedAmountDue })}
				onClick={() => setExpandedByUser((prev) => !prev)}
				data-testid="amount-due-toggle"
			>
				<AmountDueChevronWrap expanded={expanded} aria-hidden="true">
					<ExpandMoreIcon sx={chevronIconSx} />
				</AmountDueChevronWrap>
				<AmountDueLabel>{t('checkout:cart:amountDue')}</AmountDueLabel>
				<AmountDueValue>{formattedAmountDue}</AmountDueValue>
			</AmountDueButton>
		</AccordionRoot>
	)
}
